import React from "react";
import { Link } from "react-router-dom";
import type { ClientBirthdayRow } from "../../../types";
import { formatBirthdayWeekdayDayMonth, isBirthdayToday } from "../../../utils/dateUtils";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import {
  getBirthdayWhatsAppMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { Card, ErrorState, Loading, Icons } from "../../shared";

function hasBirthdayValue(birthday: string | null | undefined): boolean {
  return Boolean(birthday && String(birthday).trim());
}

export interface ClientBirthdaysPanelProps {
  title: string;
  subtitle: string;
  rangeLabel: string;
  clients: ClientBirthdayRow[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
}

export const ClientBirthdaysPanel: React.FC<ClientBirthdaysPanelProps> = ({
  title,
  subtitle,
  rangeLabel,
  clients,
  loading,
  error,
  onRetry,
}) => {
  const clientsWithBirthday = clients.filter((c) => hasBirthdayValue(c.birthday));
  const showEmpty =
    !loading && !error && (clients.length === 0 || clientsWithBirthday.length === 0);

  return (
    <Card className="p-4 sm:p-6 border border-gray-100 shadow-sm">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
        <p className="text-xs font-medium text-sky-700 mt-1">{rangeLabel}</p>
      </div>

      {loading && (
        <Loading message="Carregando aniversários…" size="md" className="py-8" />
      )}

      {!loading && error && (
        <ErrorState
          message={error}
          variant="inline"
          onRetry={onRetry}
          className="py-4"
        />
      )}

      {showEmpty && (
        <div
          className="flex flex-col items-center justify-center py-10 px-4 text-center rounded-xl bg-gray-50/90 border border-dashed border-gray-200"
          role="status"
          aria-live="polite"
        >
          <Icons.Cake sx={{ fontSize: 44 }} className="text-gray-300" />
          <h3 className="mt-3 text-base font-semibold text-gray-800">
            Nenhum aniversário
          </h3>
          <p className="mt-1.5 text-sm text-gray-500 max-w-[16rem] leading-snug">
            {clients.length > 0 && clientsWithBirthday.length === 0
              ? "Nenhum cliente neste período possui data de nascimento cadastrada."
              : "Nenhum cliente com aniversário neste período."}
          </p>
        </div>
      )}

      {!loading && !error && clientsWithBirthday.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden">
          {clientsWithBirthday.map((c) => {
            const showWhatsApp =
              isBirthdayToday(c.birthday) && Boolean(c.phoneNumber?.trim());
            return (
              <li key={c.id}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-3 sm:px-4 hover:bg-sky-50/60 transition-colors">
                  <Link
                    to={`/admin/clients/${c.id}`}
                    className="min-w-0 flex-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                  >
                    <p className="font-medium text-gray-900 truncate">{c.name}</p>
                    {c.phoneNumber ? (
                      <p className="text-sm text-gray-600">
                        {formatPhoneNumber(c.phoneNumber)}
                      </p>
                    ) : null}
                  </Link>
                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                    <p className="text-sm font-semibold text-sky-700 text-right max-w-[14rem] leading-snug">
                      {formatBirthdayWeekdayDayMonth(c.birthday!)}
                    </p>
                    {showWhatsApp ? (
                      <button
                        type="button"
                        onClick={() =>
                          sendWhatsAppMessage({
                            phoneNumber: c.phoneNumber!,
                            message: getBirthdayWhatsAppMessage(c.name),
                          })
                        }
                        className={getWhatsAppButtonStyles("sm")}
                        title="Parabenizar no WhatsApp"
                        aria-label={`Enviar parabéns por WhatsApp para ${c.name}`}
                      >
                        <svg
                          className={getWhatsAppIconStyles("sm")}
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path d={WHATSAPP_ICON_PATH} />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};
